import React, { useState } from 'react';
import { validateBookingForm } from '../utils/validators';
import './ContactForm.css';

function ContactForm({ onSubmit, initialData = {}, submitButtonText = 'Make Booking' }) {
    const [formData, setFormData] = useState({
        ...initialData,
        first_name: initialData.first_name || '',
        last_name: initialData.last_name || '',
        email: initialData.email || ''
    });

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear error for this field when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const validation = validateBookingForm(formData);
        if (!validation.isValid) {
            setErrors(validation.errors);
            return;
        }

        setIsSubmitting(true);
        try {
            await onSubmit(formData);
        } catch (error) {
            console.error('Form submission error:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const isFormValid = () => {
        // Email is optional — only first and last name are required
        return formData.first_name.trim() && formData.last_name.trim();
    };

    return (
        <form className="contact-form" onSubmit={handleSubmit} noValidate>
            <div className="form-row">
                <div className="form-group">
                    {/* H4: aria-hidden on asterisk; aria-required on input conveys required-ness to screen readers */}
                    <label htmlFor="first_name">
                        First Name{' '}
                        <span className="required-asterisk" aria-hidden="true">*</span>
                    </label>
                    <input
                        type="text"
                        id="first_name"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleChange}
                        className={errors.first_name ? 'error' : ''}
                        placeholder="John"
                        aria-required="true"
                        aria-invalid={!!errors.first_name}
                        aria-describedby={errors.first_name ? 'first_name_error' : undefined}
                    />
                    {errors.first_name && (
                        <span id="first_name_error" className="error-message" role="alert">
                            {errors.first_name}
                        </span>
                    )}
                </div>

                <div className="form-group">
                    <label htmlFor="last_name">
                        Last Name{' '}
                        <span className="required-asterisk" aria-hidden="true">*</span>
                    </label>
                    <input
                        type="text"
                        id="last_name"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleChange}
                        className={errors.last_name ? 'error' : ''}
                        placeholder="Doe"
                        aria-required="true"
                        aria-invalid={!!errors.last_name}
                        aria-describedby={errors.last_name ? 'last_name_error' : undefined}
                    />
                    {errors.last_name && (
                        <span id="last_name_error" className="error-message" role="alert">
                            {errors.last_name}
                        </span>
                    )}
                </div>
            </div>

            <div className="form-group">
                <label htmlFor="email">
                    Email <span className="optional-label">(optional)</span>
                </label>
                <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={errors.email ? 'error' : ''}
                    placeholder="john.doe@example.com"
                    aria-invalid={!!errors.email}
                    aria-describedby={errors.email ? 'email_error' : undefined}
                />
                {errors.email && (
                    <span id="email_error" className="error-message" role="alert">
                        {errors.email}
                    </span>
                )}
            </div>

            <div className="compliance-text" style={{ fontSize: '0.8rem', color: '#666', marginBottom: '1.5rem', lineHeight: '1.4' }}>
                By clicking "Make Booking," you agree to receive SMS appointment reminders from The IMA Group. Message frequency varies per appointment. Message and data rates may apply. Reply STOP to opt out, HELP for help. Consent is not a condition of purchase. View our <a href="https://theimagroup.com/privacy-statement/" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
            </div>

            <button
                type="submit"
                className="submit-button"
                disabled={!isFormValid() || isSubmitting}
            >
                {isSubmitting ? 'Processing...' : submitButtonText}
            </button>
        </form>
    );
}

export default ContactForm;
